import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { TopBar } from "@/components/TopBar";
import { TeamEditor } from "@/components/TeamEditor";
import { NotFound } from "@/components/NotFound";
import { createMatch, getMatch, updateMatch } from "@/lib/api";

const defaultTeam = (name, color) => ({
  name,
  color,
  players: [...Array.from({ length: 11 }, (_, i) => ({ number: i + 1, starter: true })),
    ...Array.from({ length: 7 }, (_, i) => ({ number: i + 12, starter: false }))],
  officials: ["Treinador", "Treinador Adjunto", "Delegado"],
});

const field = "h-12 w-full bg-[#0A0A0A] border border-white/15 px-4 focus:outline-none focus:border-[#CCFF00]";

export default function Setup() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(id ? null : {
    competition: "", date: new Date().toISOString().slice(0, 10), venue: "",
    home: defaultTeam("Casa", "vermelho"), away: defaultTeam("Fora", "azul"),
  });
  const [renames, setRenames] = useState({ home: {}, away: {} });

  useEffect(() => {
    if (id) getMatch(id).then(({ competition, date, venue, home, away }) => setForm({ competition, date, venue, home, away })).catch(() => setForm(false));
  }, [id]);

  if (form === false) return <NotFound />;
  if (!form) return null;
  const set = (k) => (v) => {
    const other = k === "home" ? "away" : k === "away" ? "home" : null;
    if (other && v.color && v.color !== form[k].color && v.color === form[other].color)
      toast.warning("As duas equipas têm a mesma cor");
    setForm((f) => ({ ...f, [k]: v }));
  };
  const rename = (side) => (old, now) => setRenames((r) => {
    const map = { ...r[side] };
    const orig = Object.keys(map).find((k) => map[k] === old) ?? old;
    if (orig === now) delete map[orig]; else map[orig] = now;
    return { ...r, [side]: map };
  });

  const submit = async () => {
    if (!["Campeonato", "Taça"].includes(form.competition)) return toast.error("Escolha a competição: Campeonato ou Taça");
    if (!form.home.name.trim() || !form.away.name.trim()) return toast.error("Indique o nome das duas equipas");
    const m = await (id ? updateMatch(id, { ...form, renames }) : createMatch(form)).catch(() => null);
    if (!m) return;
    toast.success(id ? "Configuração guardada" : "Jogo criado");
    navigate(`/jogo/${m.id}`);
  };

  return (
    <div className="relative z-10">
      <TopBar />
      <main className="max-w-7xl mx-auto px-3 sm:px-8 py-6 sm:py-14">
        <p className="label-caps mb-3">{id ? "Os eventos já registados acompanham a alteração dos números" : "Antes do apito inicial"}</p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold uppercase mb-10">{id ? "Editar jogo" : "Configurar jogo"}</h1>
        <div className="grid sm:grid-cols-[2fr_1fr] gap-4 mb-8">
          <div data-testid="match-competition">
            <span className="label-caps block mb-2">Competição</span>
            <div className="grid grid-cols-2 gap-2">
              {["Campeonato", "Taça"].map((c) => (
                <button key={c} type="button" data-testid={`match-competition-${c === "Taça" ? "taca" : "campeonato"}`} onClick={() => set("competition")(c)}
                  className={`tap h-12 border font-display text-lg font-bold uppercase ${form.competition === c ? "bg-[#CCFF00] text-black border-[#CCFF00]" : "border-white/15 hover:bg-white/5"}`}>
                  {c}
                </button>
              ))}
            </div>
            <p className="text-xs text-zinc-500 mt-2">{form.competition === "Taça" ? "Com prolongamento e penáltis se necessário" : form.competition === "Campeonato" ? "Sem prolongamento nem penáltis" : "Escolha o tipo de competição"}</p>
          </div>
          <label className="block">
            <span className="label-caps block mb-2">Data</span>
            <input data-testid="match-date" type="date" value={form.date} onChange={(e) => set("date")(e.target.value)} className={field} />
          </label>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <TeamEditor side="home" team={form.home} onChange={set("home")} onRename={rename("home")} />
          <TeamEditor side="away" team={form.away} onChange={set("away")} onRename={rename("away")} />
        </div>
        <div className="sticky bottom-0 mt-10 py-3 sm:py-4 bg-[#0A0A0A] border-t border-white/15 flex justify-end" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}>
          <button data-testid="setup-submit" onClick={submit}
            className="tap h-14 sm:h-16 px-10 w-full sm:w-auto justify-center bg-[#CCFF00] text-black font-display text-xl font-bold uppercase flex items-center gap-3 hover:brightness-110">
            {id ? "Guardar" : "Ir para o jogo"} <ArrowRight size={22} />
          </button>
        </div>
      </main>
    </div>
  );
}
